/**
 * A ZIP archive, stored rather than compressed.
 *
 * What an Office file is on disk, and small enough to write here rather than depend on a library
 * that assumes Node's `Buffer` or a browser's `Blob` — this runs in a browser, in a Worker and on
 * a phone, and all three have `Uint8Array`. Nothing is compressed: the XML in a slide deck is a
 * few kilobytes and the one picture in it is a PNG, which does not compress, so deflate would buy
 * almost nothing for the most intricate code in the file.
 *
 * The format is the plain one — a local header and the bytes for each entry, then a central
 * directory naming them all, then an end record pointing at the directory. No ZIP64, which caps
 * an archive at 4 GB; a mind map's slide deck is not going to find that limit.
 */
const encoder = new TextEncoder();
/** CRC-32 (IEEE 802.3), which ZIP stores for every entry. Table built once, on first use. */
let table = null;
function crcTable() {
    if (table !== null)
        return table;
    table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1)
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[n] = c >>> 0;
    }
    return table;
}
export function crc32(bytes) {
    const lookup = crcTable();
    let crc = 0xffffffff;
    for (const byte of bytes)
        crc = (lookup[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
}
/** 1980-01-01 00:00, the earliest date ZIP can say: an export's timestamp is not its content. */
const DOS_TIME = 0;
const DOS_DATE = (0 << 9) | (1 << 5) | 1;
/** Bit 11: names are UTF-8. Set so a non-ASCII path is read back as what it says. */
const UTF8_FLAG = 0x0800;
export function zip(entries) {
    const parts = [];
    const central = [];
    let offset = 0;
    for (const entry of entries) {
        const name = encoder.encode(entry.path);
        const data = typeof entry.data === 'string' ? encoder.encode(entry.data) : entry.data;
        const crc = crc32(data);
        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, 0x04034b50, true);
        local.setUint16(4, 20, true); // version needed: 2.0
        local.setUint16(6, UTF8_FLAG, true);
        local.setUint16(8, 0, true); // stored
        local.setUint16(10, DOS_TIME, true);
        local.setUint16(12, DOS_DATE, true);
        local.setUint32(14, crc, true);
        local.setUint32(18, data.length, true);
        local.setUint32(22, data.length, true);
        local.setUint16(26, name.length, true);
        local.setUint16(28, 0, true);
        parts.push(new Uint8Array(local.buffer), name, data);
        const header = new DataView(new ArrayBuffer(46));
        header.setUint32(0, 0x02014b50, true);
        header.setUint16(4, 20, true); // made by
        header.setUint16(6, 20, true); // needed
        header.setUint16(8, UTF8_FLAG, true);
        header.setUint16(10, 0, true);
        header.setUint16(12, DOS_TIME, true);
        header.setUint16(14, DOS_DATE, true);
        header.setUint32(16, crc, true);
        header.setUint32(20, data.length, true);
        header.setUint32(24, data.length, true);
        header.setUint16(28, name.length, true);
        header.setUint16(30, 0, true); // extra
        header.setUint16(32, 0, true); // comment
        header.setUint16(34, 0, true); // disk
        header.setUint16(36, 0, true); // internal attributes
        header.setUint32(38, 0, true); // external attributes
        header.setUint32(42, offset, true);
        central.push(new Uint8Array(header.buffer), name);
        offset += 30 + name.length + data.length;
    }
    const directorySize = central.reduce((sum, part) => sum + part.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(4, 0, true);
    end.setUint16(6, 0, true);
    end.setUint16(8, entries.length, true);
    end.setUint16(10, entries.length, true);
    end.setUint32(12, directorySize, true);
    end.setUint32(16, offset, true);
    end.setUint16(20, 0, true);
    const all = [...parts, ...central, new Uint8Array(end.buffer)];
    const out = new Uint8Array(all.reduce((sum, part) => sum + part.length, 0));
    let at = 0;
    for (const part of all) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}
//# sourceMappingURL=zip.js.map